import React from "react";
import { Text } from "../../atoms/text/text";
import { isSafeImageSrc } from "../../services/safe-image-src";
import styles from "./channel-search.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One row of the suggestion listbox — SR6, AX11.
 *
 * ⚠ `aria-selected` MEANS CHOSEN HERE, not highlighted. The listbox is
 * multi-selectable, so the highlight travels on `aria-activedescendant` and
 * this attribute answers AX11's "a screen reader announces whether each
 * suggestion is selected". `data-active` is the highlight, and it is styling
 * only.
 *
 * ⚠ `aria-posinset` / `aria-setsize` are FLAT ACROSS THE HEADINGS, and count
 * the SUGGESTIONS ONLY. Flat is what tells two rows sharing a name and a
 * number apart by position (AX11), and `role="group"` does not disturb it —
 * an authored position is used verbatim whatever contains it. Counting the
 * suggestions alone is what makes these agree with the "n suggestions
 * available" the live region says; SR5's select-all row is an action, and
 * declares a set of its own.
 *
 * ⛔ No key, no state and no comparison in this file derives from `label`. The
 * caller's `id` is the only identity; see the item contract.
 */
export interface SuggestionRowProps {
  /** The option id `aria-activedescendant` points at. */
  id: string;
  label: string;
  /** Where the matched run starts in `label`; -1 for a row with no match run. */
  matchStart: number;
  matchLength: number;
  /** Chosen (SR6's check mark), as opposed to highlighted. */
  selected: boolean;
  /** Highlighted by the arrow walk. Paint only. */
  active: boolean;
  posInSet: number;
  setSize: number;
  secondary?: string;
  iconSrc?: string;
  badge?: string;
  /**
   * Internal to this folder and not part of the molecule's published surface,
   * so the no-handler-props rule — which governs what a call site can pass a
   * DDS component — is not in play.
   */
  onActivate: () => void;
}

/** SR2 — the matched run in bold. `<b>` rather than `<strong>`: it is a
 *  typographic highlight, not emphasis a screen reader should stress. */
function MatchedLabel({
  label,
  start,
  length
}: {
  label: string;
  start: number;
  length: number;
}) {
  if (start < 0) {
    return <>{label}</>;
  }
  return (
    <>
      {label.slice(0, start)}
      <b>{label.slice(start, start + length)}</b>
      {label.slice(start + length)}
    </>
  );
}

function SuggestionRowImpl({
  id,
  label,
  matchStart,
  matchLength,
  selected,
  active,
  posInSet,
  setSize,
  secondary,
  iconSrc,
  badge,
  onActivate
}: SuggestionRowProps) {
  /* ONE POLICY FOR EVERY IMAGE SINK — the same gate `channel-logo.tsx:114`
     and the other sibling sinks put on theirs. This molecule's only
     production caller already sanitises (`lineup-search.shared.ts:123`), but
     it is published as a shared molecule, so the sink gates itself rather
     than trusting the caller. */
  const safeIconSrc = isSafeImageSrc(iconSrc) ? iconSrc : "";

  return (
    <li
      aria-posinset={posInSet}
      aria-selected={selected}
      aria-setsize={setSize}
      className={styles.option}
      data-active={active || undefined}
      id={id}
      /* ⚠ The pointer press is cancelled so focus never leaves the input: the
         listbox is driven by `aria-activedescendant`, and a row taking focus
         would blur the field and close the list before the click landed.
         Keyboard activation does not come through here — it is Enter in
         `use-combobox`, which is why this row is not a tab stop and needs no
         key handler of its own. */
      onClick={onActivate}
      onMouseDown={event => event.preventDefault()}
      role="option"
    >
      {safeIconSrc ?
        <img
          alt=""
          className={styles.optionIcon}
          decoding="async"
          loading="lazy"
          src={safeIconSrc}
        /> :
        null}
      <Text as="span" className={styles.optionLabel} variant="body-3">
        <MatchedLabel label={label} length={matchLength} start={matchStart} />
      </Text>
      {secondary ?
        <Text as="span" className={styles.optionSecondary} variant="body-3">
          {secondary}
        </Text> :
        null}
      {badge ?
        <Text as="span" className={styles.optionBadge} variant="label">
          {badge}
        </Text> :
        null}
      {selected ?
        <svg
          aria-hidden="true"
          className={styles.optionCheck}
          focusable="false"
          viewBox="0 0 16 16"
        >
          <path
            d="M3 8.5 6.5 12 13 4.5"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.75"
          />
        </svg> :
        null}
    </li>
  );
}
export const SuggestionRow = withSafeRender(SuggestionRowImpl, "SuggestionRow");

export default SuggestionRow;
